import CtaButton from '@/components/CtaButton';
import CtaText from '@/components/CtaText';
import ErrorMessage from '@/components/ErrorMessage';
import TextInput from '@/components/Forms/TextInput';
import LoadingSpinner from '@/components/Icons/LoadingSpinner';
import RightCheveron from '@/components/Icons/RightCheveron';
import PageLayout from '@/components/PageLayout';
import { NavBarLightTheme } from '@/components/PageLayout/PageNavBar';
import TwelveColGrid from '@/components/TwelveColGrid';
import useHandleErrorLogic from '@/hooks/useHandleErrorLogic';
import { ArtistApplicationFormValues } from '@/lib/interfaces';
import { phoneRegExp } from '@/utils/forms';
import { postArtistApplication } from '@/utils/v2HttpRequests';
import { Field, Form, Formik } from 'formik';
import { useState } from 'react';
import * as yup from 'yup';

const ArtistApplicationSchema = yup.object({
  name: yup.string().required('Artist Name is required'),
  firstName: yup.string().required('Legal First Name is required'),
  lastName: yup.string().required('Legal Last Name is required'),
  currentCity: yup.string().required('Current City is required'),
  email: yup.string().required('Email is required'),
  phoneNumber: yup
    .string()
    .required('Phone Number is required')
    .matches(phoneRegExp, 'Phone number is not valid'),
  instagram: yup.string().required('Instagram is required'),
  website: yup.string().required('Website is required'),
  comments: yup.string(),
});

const artistApplicationInitialValues: ArtistApplicationFormValues = {
  name: '',
  firstName: '',
  lastName: '',
  currentCity: '',
  email: '',
  phoneNumber: '',
  instagram: '',
  website: '',
  comments: '',
};

export default function ArtistApplicationPage() {
  const [err, setErr] = useHandleErrorLogic();
  const [submitted, setSubmitted] = useState(false);

  return (
    <PageLayout>
      <ErrorMessage error={err} />
      <NavBarLightTheme sticky={false} />
      <TwelveColGrid my="mt-3.2 mb-4.8 lgt:mt-6.4">
        {submitted ? (
          <div className="h-full col-span-12">
            <h3 className="col-span-12 font-medium text-center text-black-100 copy-l">
              THANK YOU
              <div className="font-normal text-s mt-0.8">
                Someone from OLEA will be reaching out shortly.
              </div>
            </h3>
          </div>
        ) : (
          <>
            <h3 className="col-span-12 font-medium text-center text-black-100 copy-l">
              ARTIST INQUIRY
              <div className="font-normal text-s mt-0.8">
                please submit your information below
              </div>
            </h3>

            <div className="mt-6.4 col-span-12 lgt:col-start-5 lgt:col-span-4">
              <Formik
                validationSchema={ArtistApplicationSchema}
                initialValues={artistApplicationInitialValues}
                onSubmit={async (values, actions) => {
                  try {
                    await postArtistApplication(values);
                    setSubmitted(true);
                  } catch (error: any) {
                    const msg = error.response.data.error.message;
                    actions.setSubmitting(false);
                    setErr(msg);
                  }
                }}
              >
                {({ isSubmitting, isValid }) => (
                  <Form className="flex flex-col">
                    <Field
                      type="text"
                      name="name"
                      placeholder=" "
                      labelCopy="Artist name"
                      component={TextInput}
                    />
                    <Field
                      type="text"
                      name="firstName"
                      placeholder=" "
                      labelCopy="Legal first name"
                      component={TextInput}
                    />
                    <Field
                      type="text"
                      name="lastName"
                      placeholder=" "
                      labelCopy="Legal last name"
                      component={TextInput}
                    />
                    <Field
                      type="text"
                      name="currentCity"
                      placeholder=" "
                      labelCopy="Current city"
                      component={TextInput}
                    />
                    <Field
                      type="email"
                      name="email"
                      placeholder=" "
                      labelCopy="Email"
                      component={TextInput}
                    />
                    <Field
                      type="tel"
                      name="phoneNumber"
                      placeholder=" "
                      labelCopy="Phone"
                      component={TextInput}
                    />
                    <Field
                      type="text"
                      name="instagram"
                      placeholder=" "
                      labelCopy="Instagram"
                      component={TextInput}
                    />
                    <Field
                      type="text"
                      name="website"
                      placeholder=" "
                      labelCopy="Website"
                      component={TextInput}
                    />
                    <Field
                      type="text"
                      name="comments"
                      placeholder=" "
                      labelCopy="Comments"
                      component={TextInput}
                    />

                    <CtaButton
                      height="h-4.8"
                      width="w-34.4 lgt:w-36.0"
                      type="submit"
                      disabled={isSubmitting || !isValid}
                    >
                      {isSubmitting ? (
                        <>
                          <CtaText>PROCESSING</CtaText>
                          <LoadingSpinner />
                        </>
                      ) : (
                        <>
                          <CtaText>SUBMIT</CtaText>
                          <RightCheveron />
                        </>
                      )}
                    </CtaButton>
                  </Form>
                )}
              </Formik>
            </div>
          </>
        )}
      </TwelveColGrid>
    </PageLayout>
  );
}
